import React, {useState, useEffect, useRef} from "react";
import { useNavigate } from "react-router-dom";
import { AuthService, MessageService, CustomerService, EventsService, ResourceService, formatCustomerDisplayName } from "../../services";
import { Breadcrumb, Tabs, Tab, Modal, Button, Dropdown, Spinner } from "react-bootstrap";
import { PencilSquare, Plus, Trash, Send, Eye, ArrowLeft, Filter, Calendar3, Search, ChevronLeft, ChevronRight, Clock, ClockHistory } from "react-bootstrap-icons";
import Select from 'react-select';
import { MultiSelectDropdown, formatMedicalProviderAddressDisplay, formatResourceSpecialtyDisplay, formatSpecialtyLabel } from '../../shared';
import { PREFERRED_TEXT_LANGUAGE, PREFERRED_TEXT_LANGUAGE_TEXT, CUSTOMER_TYPE, isCustomerListActive } from '../../shared/constants/customer.constant';
import { getSiteDisplayName } from "../../shared/constants/siteDisplayNames";
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';

const APPOINTMENT_REMINDER_ENGLISH_DEFAULT = `Date & Time: {{day_of_week}}, {{date}} at {{time}}
Provider: {{provider_name}}
Specialty: {{specialty}}
Address: {{provider_address}}
Special Instructions: {{requirement}}`;

const APPOINTMENT_REMINDER_CHINESE_DEFAULT = `日期和时间：{{day_of_week_chinese}} {{date_chinese}} {{time_chinese}}
医生/医疗机构：{{provider_name}}
专科：{{specialty}}
地址：{{provider_address}}
注意事项：{{requirement}}`;

const APPOINTMENT_REMINDER_ENGLISH_STARTING_DEFAULT = `Hi {{preferred_name}}, this is a reminder from Worldshine Adult Medical Daycare Center about your upcoming medical appointments:`;

const APPOINTMENT_REMINDER_CHINESE_STARTING_DEFAULT = `{{preferred_name}} 您好，这里是阳光保健养生中心，温馨提醒您近期有以下看诊预约：`;

const APPOINTMENT_REMINDER_ENGLISH_ENDING_DEFAULT = `Appointment times may change. Nursing will confirm with you the day before your visit. Please use the final confirmed time. If you have questions, please visit our Nursing Station.`;

const APPOINTMENT_REMINDER_CHINESE_ENDING_DEFAULT = `预约时间可能会有调整，护理部将在看诊前一天与您确认，请以最终确认时间为准。如有疑问，请到阳光保健养生中心护士站询问。`;

const AUTO_SEND_WEEKDAY_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const APPOINTMENT_REMINDER_PLACEHOLDERS = [
  { label: "Preferred Name", token: "{{preferred_name}}" },
  { label: "First Name", token: "{{first_name}}" },
  { label: "Last Name", token: "{{last_name}}" },
  { label: "Day of Week", token: "{{day_of_week}}" },
  { label: "Date", token: "{{date}}" },
  { label: "Time", token: "{{time}}" },
  { label: "Chinese Date", token: "{{date_chinese}}" },
  { label: "Chinese Day of Week", token: "{{day_of_week_chinese}}" },
  { label: "Chinese Time", token: "{{time_chinese}}" },
  { label: "Provider Name", token: "{{provider_name}}" },
  { label: "Specialty", token: "{{specialty}}" },
  { label: "Provider Address", token: "{{provider_address}}" },
  { label: "Requirement", token: "{{requirement}}" },
];

const getMedicalEventCustomerId = (event) => {
  const candidate = event?.data?.customer ?? event?.target_uuid;
  if (candidate == null || candidate === "") return "";
  if (typeof candidate === "object") {
    return `${candidate._id || candidate.id || ""}`.trim();
  }
  return `${candidate}`.trim();
};

const isCustomerEventConfirmed = (event) => {
  if (event?.confirmed === true) return true;
  const top = `${event?.confirmed ?? ""}`.trim().toLowerCase();
  if (top === "true" || top === "yes") return true;
  const nested = event?.data?.confirmed;
  if (nested === true) return true;
  const nestedText = `${nested ?? ""}`.trim().toLowerCase();
  return nestedText === "true" || nestedText === "yes";
};

const customerHasTextConsent = (customer) => {
  const consent = `${customer?.consent_to_text_messages || ""}`.trim().toLowerCase();
  if (consent === "yes") return true;
  return customer?.text_msg_enabled === true;
};

const getPreferredTextLanguageLabel = (customer) => {
  const raw = `${customer?.preferred_text_language || ""}`.trim().toLowerCase();
  if (!raw) return "English";
  return PREFERRED_TEXT_LANGUAGE_TEXT[raw] || PREFERRED_TEXT_LANGUAGE_TEXT[customer?.preferred_text_language] || "English";
};

const CHINESE_WEEKDAYS = ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"];

const normalizeProviderText = (value) =>
  `${value || ""}`
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const isFriendlyReminderProvider = (event, resource) => {
  const values = [
    resource?.name,
    resource?.type,
    resource?.type_other,
    event?.source_name,
    event?.source_type,
    event?.data?.resource_name,
    event?.data?.resource_type,
    event?.data?.resource_type_other,
  ];
  return values.some((value) => {
    const normalized = normalizeProviderText(value);
    if (!normalized) return false;
    return normalized.includes("friendly reminder") || normalized.includes("友情提示");
  });
};

const getCustomerLookupIds = (customer) =>
  [`${customer?.id || ""}`.trim(), `${customer?._id || ""}`.trim()].filter(Boolean);

const fillAppointmentTemplate = (template, values) => {
  let result = `${template || ""}`;
  Object.entries(values || {}).forEach(([token, value]) => {
    result = result.split(token).join(`${value ?? ""}`);
  });
  return result.trim();
};

const getCustomerPreferredName = (customer) => {
  const preferred = `${customer?.name_cn || customer?.preferred_name || customer?.preferredName || ""}`.trim();
  if (preferred) return preferred;
  const firstName = `${customer?.firstname || customer?.firstName || ""}`.trim();
  const lastName = `${customer?.lastname || customer?.lastName || ""}`.trim();
  const fromParts = [firstName, lastName].filter(Boolean).join(" ");
  if (fromParts) return fromParts;
  return `${customer?.name || ""}`.trim();
};

const normalizeMessagePlaceholderKey = (token) =>
  `${token || ""}`
    .replace(/[{}]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const CUSTOMER_PLACEHOLDER_CANONICAL = {
  preferred_name: "preferred_name",
  preferredname: "preferred_name",
  name: "preferred_name",
  first_name: "first_name",
  firstname: "first_name",
  last_name: "last_name",
  lastname: "last_name",
};

const MESSAGE_CUSTOMER_PLACEHOLDERS = [
  { label: "Preferred Name", token: "{{preferred_name}}" },
  { label: "First Name", token: "{{first_name}}" },
  { label: "Last Name", token: "{{last_name}}" },
];

const isCustomerBoundPlaceholder = (token) =>
  Boolean(CUSTOMER_PLACEHOLDER_CANONICAL[normalizeMessagePlaceholderKey(token)]);

const getCustomerMessagePlaceholderValues = (customer) => {
  const firstName = `${customer?.firstname || customer?.firstName || ""}`.trim();
  const lastName = `${customer?.lastname || customer?.lastName || ""}`.trim();
  return {
    preferred_name: getCustomerPreferredName(customer),
    first_name: firstName,
    last_name: lastName,
  };
};

const extractPlaceholdersFromText = (text) => {
  const source = `${text || ""}`;
  const tokens = [];
  const seen = new Set();
  const addToken = (raw) => {
    const token = `${raw || ""}`.trim();
    if (!token || seen.has(token)) return;
    seen.add(token);
    tokens.push(token);
  };
  const doubleRe = /\{\{\s*([^}]+?)\s*\}\}/g;
  let match;
  while ((match = doubleRe.exec(source)) !== null) addToken(match[1]);
  const withoutDoubles = source.replace(doubleRe, "");
  const singleRe = /\{([^{}]+)\}/g;
  while ((match = singleRe.exec(withoutDoubles)) !== null) addToken(match[1]);
  return tokens;
};

const buildAppointmentPlaceholderValues = (customer, appointment) => {
  const start = appointment?.start ? moment(appointment.start) : null;
  const validStart = start?.isValid() ? start : null;
  const firstName = `${customer?.firstname || customer?.firstName || ""}`.trim();
  const lastName = `${customer?.lastname || customer?.lastName || ""}`.trim();
  return {
    "{{preferred_name}}": getCustomerPreferredName(customer),
    "{{first_name}}": firstName,
    "{{last_name}}": lastName,
    "{{day_of_week}}": validStart ? validStart.format("dddd") : "",
    "{{date}}": validStart ? validStart.format("MM/DD/YYYY") : "",
    "{{time}}": validStart ? validStart.format("h:mm A") : "",
    "{{date_chinese}}": validStart ? validStart.format("YYYY年M月D日") : "",
    "{{day_of_week_chinese}}": validStart ? CHINESE_WEEKDAYS[validStart.day()] || "" : "",
    "{{time_chinese}}": validStart
      ? `${validStart.hour() < 12 ? "上午" : "下午"}${validStart.format("h:mm")}`
      : "",
    "{{provider_name}}": appointment?.providerName || "",
    "{{specialty}}": appointment?.specialty || "",
    "{{provider_address}}": appointment?.providerAddress || "",
    "{{requirement}}": appointment?.requirement || "",
  };
};

const buildAppointmentReminderPreview = (row, templates) => {
  const appointments = (Array.isArray(row?.appointments) ? row.appointments : []).filter(
    (appointment) => appointment?.confirmed !== false
  );
  const language =
    `${row?.preferredTextLanguageRaw || ""}`.trim().toLowerCase() === PREFERRED_TEXT_LANGUAGE.CHINESE
      ? "chinese"
      : "english";
  const startingTemplate =
    language === "chinese" ? templates.chineseStarting : templates.englishStarting;
  const appointmentTemplate =
    language === "chinese" ? templates.chineseAppointment : templates.englishAppointment;
  const endingTemplate =
    language === "chinese" ? templates.chineseEnding : templates.englishEnding;
  const headingPrefix = language === "chinese" ? "预约" : "Appointment";
  const numberAppointments = appointments.length > 1;
  const messages = appointments.map((appointment, index) => {
    const values = buildAppointmentPlaceholderValues(row, appointment);
    const starting = fillAppointmentTemplate(startingTemplate, values);
    const body = fillAppointmentTemplate(appointmentTemplate, values);
    const ending = fillAppointmentTemplate(endingTemplate, values);
    const appointmentBody = numberAppointments
      ? `${headingPrefix} ${index + 1}\n${body}`.trim()
      : body;
    return [starting, appointmentBody, ending]
      .filter((part) => `${part || ""}`.trim())
      .join("\n\n");
  });
  if (!messages.length) {
    const fallback = [
      fillAppointmentTemplate(startingTemplate, buildAppointmentPlaceholderValues(row, {})),
      fillAppointmentTemplate(endingTemplate, buildAppointmentPlaceholderValues(row, {})),
    ]
      .filter((part) => `${part || ""}`.trim())
      .join("\n\n");
    if (fallback) messages.push(fallback);
  }
  return {
    language,
    languageLabel: language === "chinese" ? "Chinese" : "English",
    messages,
    appointments,
  };
};

const TEXTMAGIC_UNICODE_LIMIT = 402;
const TEXTMAGIC_PART_PREFIX_RESERVE = 8;

const unicodeCharLength = (text) => [...`${text || ""}`].length;

const partPrefix = (index, total) => `(${index}/${total})\n`;

const splitCharsPreferBreak = (chars, chunkSize) => {
  const parts = [];
  let i = 0;
  while (i < chars.length) {
    let end = Math.min(i + chunkSize, chars.length);
    if (end < chars.length && chunkSize > 1) {
      const windowStart = Math.max(i, end - 40);
      for (let j = end - 1; j >= windowStart; j -= 1) {
        if (/\s/.test(chars[j])) {
          end = j + 1;
          break;
        }
      }
    }
    if (end <= i) end = Math.min(i + chunkSize, chars.length);
    parts.push(chars.slice(i, end).join(""));
    i = end;
  }
  return parts;
};

const splitMessageIntoSmsParts = (text) => {
  const chars = [...`${text || ""}`];
  if (chars.length === 0) return [];
  if (chars.length <= TEXTMAGIC_UNICODE_LIMIT) {
    return [{ text: chars.join(""), weight: chars.length }];
  }
  const rawParts = splitCharsPreferBreak(
    chars,
    TEXTMAGIC_UNICODE_LIMIT - TEXTMAGIC_PART_PREFIX_RESERVE
  );
  return rawParts.map((part, index) => {
    const labeled = `${partPrefix(index + 1, rawParts.length)}${part}`;
    return { text: labeled, weight: unicodeCharLength(labeled) };
  });
};

const digitsOnlyPhone = (value) => `${value || ""}`.replace(/\D/g, "");

const formatUsPhoneInput = (value) => {
  const phoneNumber = digitsOnlyPhone(value).slice(0, 11);
  const local = phoneNumber.length === 11 && phoneNumber.startsWith("1")
    ? phoneNumber.slice(1)
    : phoneNumber.slice(0, 10);
  if (local.length < 4) return local;
  if (local.length < 7) return `(${local.slice(0, 3)})${local.slice(3)}`;
  return `(${local.slice(0, 3)})${local.slice(3, 6)}-${local.slice(6, 10)}`;
};

const toTextMagicPhone = (value) => {
  const digits = digitsOnlyPhone(value);
  if (!digits) return "";
  if (digits.length === 10) return `1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return digits;
  return digits;
};

const getCustomerSmsPhone = (customer) =>
  toTextMagicPhone(customer?.phone || customer?.mobile_phone || customer?.home_phone);

const MessageList = () => {
  const navigate = useNavigate();
  const canEditMessagingTemplate = AuthService.hasPermission('Create&Edit_Messaging Template');
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('allMessages');

  // Custom template state
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateTitle, setTemplateTitle] = useState('');
  const [templateChinese, setTemplateChinese] = useState('');
  const [templateEnglish, setTemplateEnglish] = useState('');

  // Send message state
  const [showSendModal, setShowSendModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [eligibleCustomers, setEligibleCustomers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sendTitle, setSendTitle] = useState('');
  const [sendChinese, setSendChinese] = useState('');
  const [sendEnglish, setSendEnglish] = useState('');

  // Preview state
  const [placeholders, setPlaceholders] = useState([]);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [sending, setSending] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [showScheduledJobsModal, setShowScheduledJobsModal] = useState(false);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [loadingScheduledJobs, setLoadingScheduledJobs] = useState(false);
  const [cancellingJobId, setCancellingJobId] = useState("");

  // Search, Filter, Date state for All Sent Messages
  const [searchText, setSearchText] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterDate, setFilterDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);

  // Pagination state
  const [sentPage, setSentPage] = useState(1);
  const [sentPageSize, setSentPageSize] = useState(10);
  const [templatePage, setTemplatePage] = useState(1);
  const [templatePageSize, setTemplatePageSize] = useState(10);

  const [showAppointmentTemplateModal, setShowAppointmentTemplateModal] = useState(false);
  const [appointmentReminderEnglish, setAppointmentReminderEnglish] = useState(APPOINTMENT_REMINDER_ENGLISH_DEFAULT);
  const [appointmentReminderChinese, setAppointmentReminderChinese] = useState(APPOINTMENT_REMINDER_CHINESE_DEFAULT);
  const [appointmentReminderEnglishStarting, setAppointmentReminderEnglishStarting] = useState(APPOINTMENT_REMINDER_ENGLISH_STARTING_DEFAULT);
  const [appointmentReminderChineseStarting, setAppointmentReminderChineseStarting] = useState(APPOINTMENT_REMINDER_CHINESE_STARTING_DEFAULT);
  const [appointmentReminderEnglishEnding, setAppointmentReminderEnglishEnding] = useState(APPOINTMENT_REMINDER_ENGLISH_ENDING_DEFAULT);
  const [appointmentReminderChineseEnding, setAppointmentReminderChineseEnding] = useState(APPOINTMENT_REMINDER_CHINESE_ENDING_DEFAULT);
  const [activeAppointmentTemplateField, setActiveAppointmentTemplateField] = useState("chineseAppointment");
  const [savingAppointmentTemplate, setSavingAppointmentTemplate] = useState(false);
  const appointmentTemplateRefs = useRef({});
  const [appointmentReminderRows, setAppointmentReminderRows] = useState([]);
  const [loadingAppointmentReminderList, setLoadingAppointmentReminderList] = useState(false);
  const [appointmentReminderPage, setAppointmentReminderPage] = useState(1);
  const [appointmentReminderPageSize, setAppointmentReminderPageSize] = useState(10);
  const [appointmentPreviewRow, setAppointmentPreviewRow] = useState(null);
  const [showTestMessageModal, setShowTestMessageModal] = useState(false);
  const [testSendToCustomer, setTestSendToCustomer] = useState(false);
  const [testSendToCustomNumber, setTestSendToCustomNumber] = useState(false);
  const [testCustomNumber, setTestCustomNumber] = useState("");
  const [sendingTestMessage, setSendingTestMessage] = useState(false);
  const [testSendDebug, setTestSendDebug] = useState(null);
  const [showSendAllModal, setShowSendAllModal] = useState(false);
  const [sendAllConfirmText, setSendAllConfirmText] = useState("");
  const [sendingAllMessages, setSendingAllMessages] = useState(false);
  const [showAutoSendScheduleModal, setShowAutoSendScheduleModal] = useState(false);
  const [autoSendSchedule, setAutoSendSchedule] = useState(null);
  const [autoSendWeekday, setAutoSendWeekday] = useState(1);
  const [autoSendTime, setAutoSendTime] = useState("09:00");
  const [autoSendEnabled, setAutoSendEnabled] = useState(true);
  const [loadingAutoSendSchedule, setLoadingAutoSendSchedule] = useState(false);
  const [savingAutoSendSchedule, setSavingAutoSendSchedule] = useState(false);
  const [skippingAutoSend, setSkippingAutoSend] = useState(false);

  useEffect(() => {
    if (!AuthService.canAddOrEditRoutes() && !AuthService.canViewRoutes() &&!AuthService.canAccessLegacySystem()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change a dispatcher or admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    fetchMessages();
    fetchCustomTemplates();
    fetchAppointmentReminderTemplate();
    fetchAppointmentReminderQualifiedCustomers();
    fetchAutoSendSchedule();
    CustomerService.getAllCustomers().then(data => {
      const all = data?.data || [];
      setEligibleCustomers(
        all
          .filter((customer) => {
            const type = `${customer?.type || ""}`.trim().toLowerCase();
            return (
              isCustomerListActive(customer) &&
              type === CUSTOMER_TYPE.MEMBER &&
              customerHasTextConsent(customer)
            );
          })
          .sort((a, b) => `${a?.name || ""}`.localeCompare(`${b?.name || ""}`, undefined, { sensitivity: "base" }))
      );
    });
  }, []);

  // Close date picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchMessages = () => {
    MessageService.getMessages().then(data => {
      setMessages(data?.data || []);
    });
  };

  const fetchCustomTemplates = () => {
    MessageService.getCustomTemplates().then(data => {
      setCustomTemplates(data?.data || []);
    });
  };

  const applyAutoSendSchedule = (schedule) => {
    if (schedule && schedule.weekday != null) {
      setAutoSendSchedule(schedule);
      setAutoSendWeekday(Number(schedule.weekday));
      setAutoSendTime(schedule.time || "09:00");
      setAutoSendEnabled(schedule.enabled !== false);
      return;
    }
    setAutoSendSchedule(null);
    setAutoSendEnabled(true);
  };

  const persistAutoSendSchedule = async ({ weekday, time, enabled }) => {
    const userName = localStorage.getItem("user") && JSON.parse(localStorage.getItem("user"))?.name;
    const response = await MessageService.saveAppointmentReminderSchedule({
      weekday: Number(weekday),
      time,
      enabled,
      update_by: userName,
    });
    applyAutoSendSchedule(response?.data?.data || null);
    return response;
  };

  const fetchAutoSendSchedule = () => {
    setLoadingAutoSendSchedule(true);
    MessageService.getAppointmentReminderSchedule()
      .then(({ data }) => {
        applyAutoSendSchedule(data || null);
      })
      .catch((error) => {
        console.error("Unable to load appointment reminder schedule:", error);
      })
      .finally(() => {
        setLoadingAutoSendSchedule(false);
      });
  };

  const openAutoSendScheduleModal = () => {
    setShowAutoSendScheduleModal(true);
    fetchAutoSendSchedule();
  };

  const closeAutoSendScheduleModal = () => {
    if (savingAutoSendSchedule || skippingAutoSend) return;
    setShowAutoSendScheduleModal(false);
  };

  const handleSaveAutoSendSchedule = async () => {
    if (savingAutoSendSchedule || skippingAutoSend) return;
    if (autoSendWeekday == null || Number.isNaN(Number(autoSendWeekday))) {
      window.alert("Pick a day of the week.");
      return;
    }
    if (!`${autoSendTime || ""}`.trim()) {
      window.alert("Pick a time in Eastern Time.");
      return;
    }
    setSavingAutoSendSchedule(true);
    try {
      await persistAutoSendSchedule({
        weekday: Number(autoSendWeekday),
        time: autoSendTime,
        enabled: autoSendEnabled,
      });
    } catch (error) {
      window.alert(
        error?.response?.data?.message || error?.message || "Unable to save the auto send schedule."
      );
    } finally {
      setSavingAutoSendSchedule(false);
    }
  };

  const handleToggleAutoSendEnabled = async (checked) => {
    setAutoSendEnabled(checked);
    if (!autoSendSchedule || savingAutoSendSchedule || skippingAutoSend) return;
    setSavingAutoSendSchedule(true);
    try {
      await persistAutoSendSchedule({
        weekday: Number(autoSendWeekday),
        time: autoSendTime,
        enabled: checked,
      });
    } catch (error) {
      setAutoSendEnabled(!checked);
      window.alert(
        error?.response?.data?.message || error?.message || "Unable to update auto send."
      );
    } finally {
      setSavingAutoSendSchedule(false);
    }
  };

  const handleSetAutoSendSkip = async (skipNext) => {
    if (!autoSendSchedule || autoSendSchedule.enabled === false || savingAutoSendSchedule || skippingAutoSend) return;
    const userName = localStorage.getItem("user") && JSON.parse(localStorage.getItem("user"))?.name;
    setSkippingAutoSend(true);
    try {
      const response = await MessageService.setAppointmentReminderScheduleSkip({
        skip_next: skipNext,
        update_by: userName,
      });
      applyAutoSendSchedule(response?.data?.data || null);
    } catch (error) {
      window.alert(
        error?.response?.data?.message || error?.message || "Unable to update skip for the next send."
      );
    } finally {
      setSkippingAutoSend(false);
    }
  };

  const fetchAppointmentReminderTemplate = () => {
    MessageService.getAppointmentReminderTemplate()
      .then(({ data }) => {
        if (!data) return;
        setAppointmentReminderEnglish(data.english_template || APPOINTMENT_REMINDER_ENGLISH_DEFAULT);
        setAppointmentReminderChinese(data.chinese_template || APPOINTMENT_REMINDER_CHINESE_DEFAULT);
        setAppointmentReminderEnglishStarting(data.english_starting_text || APPOINTMENT_REMINDER_ENGLISH_STARTING_DEFAULT);
        setAppointmentReminderChineseStarting(data.chinese_starting_text || APPOINTMENT_REMINDER_CHINESE_STARTING_DEFAULT);
        setAppointmentReminderEnglishEnding(data.english_ending_text || APPOINTMENT_REMINDER_ENGLISH_ENDING_DEFAULT);
        setAppointmentReminderChineseEnding(data.chinese_ending_text || APPOINTMENT_REMINDER_CHINESE_ENDING_DEFAULT);
      })
      .catch((error) => {
        console.error("Unable to load appointment reminder template:", error);
      });
  };

  const fetchAppointmentReminderQualifiedCustomers = () => {
    setLoadingAppointmentReminderList(true);
    // Next calendar week: Monday through Sunday after the current ISO week.
    const fromDate = moment().startOf("isoWeek").add(1, "week").toDate();
    const toDate = moment().startOf("isoWeek").add(1, "week").add(6, "days").toDate();

    Promise.all([
      CustomerService.getAllActiveCustomers(),
      EventsService.getAllEvents({
        from: EventsService.formatDate(fromDate),
        to: EventsService.formatDate(toDate),
        type: "medical",
      }),
      ResourceService.getAll(),
    ])
      .then(([customersRes, eventsRes, resourcesRes]) => {
        const customers = Array.isArray(customersRes?.data) ? customersRes.data : [];
        const events = Array.isArray(eventsRes?.data) ? eventsRes.data : [];
        const resources = Array.isArray(resourcesRes?.data) ? resourcesRes.data : [];
        const resourceById = new Map(
          resources
            .map((resource) => [String(resource?.id || resource?._id || "").trim(), resource])
            .filter(([id]) => id)
        );
        const appointmentsByCustomerId = {};

        events.forEach((event) => {
          if (`${event?.status || "active"}`.toLowerCase() === "inactive") return;
          if (!isCustomerEventConfirmed(event)) return;
          const resourceId = `${event?.data?.resource || event?.source_uuid || ""}`.trim();
          const resource = resourceId ? resourceById.get(resourceId) : null;
          if (isFriendlyReminderProvider(event, resource)) return;
          const customerId = getMedicalEventCustomerId(event);
          if (!customerId) return;
          const start = event?.start_time || event?.start || "";
          const appointment = {
            start,
            providerName: `${resource?.name || event?.data?.resource_name || event?.source_name || ""}`.trim(),
            specialty: formatResourceSpecialtyDisplay(resource) || formatSpecialtyLabel(event?.data?.resource_specialty, event?.data?.resource_specialty_other),
            providerAddress: formatMedicalProviderAddressDisplay(resource, event?.data),
            requirement: `${event?.data?.other || event?.data?.requirement || ""}`.trim(),
            confirmed: true,
          };
          if (!appointmentsByCustomerId[customerId]) appointmentsByCustomerId[customerId] = [];
          appointmentsByCustomerId[customerId].push(appointment);
        });

        Object.keys(appointmentsByCustomerId).forEach((customerId) => {
          appointmentsByCustomerId[customerId].sort((a, b) => {
            const aTime = moment(a.start);
            const bTime = moment(b.start);
            if (!aTime.isValid()) return 1;
            if (!bTime.isValid()) return -1;
            return aTime.valueOf() - bTime.valueOf();
          });
        });

        const rows = customers
          .filter((customer) => {
            if (!customerHasTextConsent(customer)) return false;
            if (!`${customer?.phone || ""}`.trim()) return false;
            const appointments = getCustomerLookupIds(customer)
              .flatMap((id) => appointmentsByCustomerId[id] || []);
            return appointments.length > 0;
          })
          .map((customer) => {
            const seen = new Set();
            const appointments = getCustomerLookupIds(customer)
              .flatMap((id) => appointmentsByCustomerId[id] || [])
              .filter((appointment) => {
                const key = `${appointment.start}|${appointment.providerName}|${appointment.specialty}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });
            return {
              id: customer.id || customer._id,
              name: formatCustomerDisplayName(customer),
              firstname: customer.firstname || customer.firstName || "",
              lastname: customer.lastname || customer.lastName || "",
              preferredName: `${customer.name_cn || ""}`.trim(),
              name_cn: customer.name_cn || "",
              preferredTextLanguage: getPreferredTextLanguageLabel(customer),
              preferredTextLanguageRaw: `${customer?.preferred_text_language || ""}`.trim().toLowerCase(),
              primaryPhone: `${customer.phone || ""}`.trim(),
              appointmentCount: appointments.length,
              appointments,
            };
          })
          .sort((a, b) =>
            `${a.name || ""}`.localeCompare(`${b.name || ""}`, undefined, { sensitivity: "base" })
          );

        setAppointmentReminderRows(rows);
        setAppointmentReminderPage(1);
      })
      .catch((error) => {
        console.error("Unable to load appointment reminder customers:", error);
        setAppointmentReminderRows([]);
      })
      .finally(() => {
        setLoadingAppointmentReminderList(false);
      });
  };

  const insertAppointmentPlaceholder = (token) => {
    const field = activeAppointmentTemplateField;
    const textarea = appointmentTemplateRefs.current[field];
    const currentValueByField = {
      chineseStarting: appointmentReminderChineseStarting,
      chineseAppointment: appointmentReminderChinese,
      chineseEnding: appointmentReminderChineseEnding,
      englishStarting: appointmentReminderEnglishStarting,
      englishAppointment: appointmentReminderEnglish,
      englishEnding: appointmentReminderEnglishEnding,
    };
    const setterByField = {
      chineseStarting: setAppointmentReminderChineseStarting,
      chineseAppointment: setAppointmentReminderChinese,
      chineseEnding: setAppointmentReminderChineseEnding,
      englishStarting: setAppointmentReminderEnglishStarting,
      englishAppointment: setAppointmentReminderEnglish,
      englishEnding: setAppointmentReminderEnglishEnding,
    };
    const currentValue = currentValueByField[field] ?? appointmentReminderChinese;
    const setValue = setterByField[field] || setAppointmentReminderChinese;
    const start = textarea?.selectionStart ?? currentValue.length;
    const end = textarea?.selectionEnd ?? currentValue.length;
    setValue(`${currentValue.slice(0, start)}${token}${currentValue.slice(end)}`);

    window.requestAnimationFrame(() => {
      if (!textarea) return;
      textarea.focus();
      const cursor = start + token.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const saveAppointmentTemplate = async () => {
    if (!canEditMessagingTemplate || savingAppointmentTemplate) return;
    setSavingAppointmentTemplate(true);
    try {
      await MessageService.saveAppointmentReminderTemplate({
        english_template: appointmentReminderEnglish,
        chinese_template: appointmentReminderChinese,
        english_starting_text: appointmentReminderEnglishStarting,
        chinese_starting_text: appointmentReminderChineseStarting,
        english_ending_text: appointmentReminderEnglishEnding,
        chinese_ending_text: appointmentReminderChineseEnding,
      });
      setShowAppointmentTemplateModal(false);
    } catch (error) {
      console.error("Unable to save appointment reminder template:", error);
      window.alert(
        error?.response?.data?.message || "Unable to save the appointment reminder template."
      );
    } finally {
      setSavingAppointmentTemplate(false);
    }
  };

  // ---- Custom Template CRUD ----
  const openCreateTemplateModal = () => {
    if (!canEditMessagingTemplate) return;
    setEditingTemplate(null);
    setTemplateTitle('');
    setTemplateChinese('');
    setTemplateEnglish('');
    setShowCreateTemplateModal(true);
  };

  const openEditTemplateModal = (template) => {
    if (!canEditMessagingTemplate) return;
    setEditingTemplate(template);
    setTemplateTitle(template.title || '');
    setTemplateChinese(template.chinese || '');
    setTemplateEnglish(template.english || '');
    setShowCreateTemplateModal(true);
  };

  const closeTemplateModal = () => {
    setShowCreateTemplateModal(false);
    setEditingTemplate(null);
    setTemplateTitle('');
    setTemplateChinese('');
    setTemplateEnglish('');
  };

  const handleSaveTemplate = () => {
    if (!canEditMessagingTemplate) return;
    if (!templateTitle.trim()) { window.alert('Template Title is required.'); return; }
    if (!templateChinese.trim()) { window.alert('Message Content (Chinese) is required.'); return; }
    if (!templateEnglish.trim()) { window.alert('Message Content (English) is required.'); return; }
    const userName = localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name;
    const data = { title: templateTitle.trim(), chinese: templateChinese.trim(), english: templateEnglish.trim() };
    if (editingTemplate) {
      data.edit_by = userName;
      data.edit_date = new Date();
      MessageService.updateCustomTemplate(editingTemplate.id, data).then(() => { fetchCustomTemplates(); closeTemplateModal(); });
    } else {
      data.create_by = userName;
      data.create_date = new Date();
      MessageService.createCustomTemplate(data).then(() => { fetchCustomTemplates(); closeTemplateModal(); });
    }
  };

  const handleDeleteTemplate = (id) => {
    if (!canEditMessagingTemplate) return;
    if (window.confirm('Are you sure you want to delete this template?')) {
      MessageService.deleteCustomTemplate(id).then(() => { fetchCustomTemplates(); });
    }
  };

  // ---- Send New Message ----
  const openSendModal = () => {
    setSelectedRecipients([]);
    setSelectedTemplate(null);
    setSendTitle('');
    setSendChinese('');
    setSendEnglish('');
    setShowSendModal(true);
  };

  const closeSendModal = () => {
    setShowSendModal(false);
    setSelectedRecipients([]);
    setSelectedTemplate(null);
    setSendTitle('');
    setSendChinese('');
    setSendEnglish('');
  };

  const handleTemplateSelect = (option) => {
    setSelectedTemplate(option);
    if (option && option.value) {
      const tmpl = customTemplates.find(t => t.id === option.value);
      if (tmpl) {
        setSendTitle(tmpl.title || '');
        setSendChinese(tmpl.chinese || '');
        setSendEnglish(tmpl.english || '');
      }
    }
  };

  const handleSelectAll = () => {
    if (selectedRecipients.length === eligibleCustomers.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(eligibleCustomers.map(c => ({ value: c.id, label: c.name })));
    }
  };

  const extractPlaceholders = (chinese, english) => [
    ...new Set([
      ...extractPlaceholdersFromText(chinese),
      ...extractPlaceholdersFromText(english),
    ]),
  ];

  const fillInPlaceholders = placeholders.filter((token) => !isCustomerBoundPlaceholder(token));

  const openPreview = () => {
    if (!selectedRecipients.length) { window.alert('Please select at least one recipient.'); return; }
    if (!sendChinese.trim() && !sendEnglish.trim()) { window.alert('Please enter message content.'); return; }
    const phs = extractPlaceholders(sendChinese, sendEnglish);
    setPlaceholders(phs);
    const initValues = {};
    phs.forEach(p => { initValues[p] = ''; });
    setPlaceholderValues(initValues);
    setShowScheduleForm(false);
    setScheduleDate("");
    setScheduleTime("");
    setShowSendModal(false);
    setShowPreviewModal(true);
  };

  const backToEditing = () => {
    setShowPreviewModal(false);
    setShowScheduleForm(false);
    setShowSendModal(true);
  };

  const findRecipientCustomer = (recipient) => {
    const rid = `${recipient?.value || ""}`;
    if (!rid) return null;
    return eligibleCustomers.find((c) => `${c?.id || ""}` === rid || `${c?._id || ""}` === rid) || null;
  };

  const replacePlaceholders = (text, customer) => {
    if (!text) return "";
    const customerVals = getCustomerMessagePlaceholderValues(customer);
    const resolveInner = (inner, original) => {
      const key = normalizeMessagePlaceholderKey(inner);
      const canonical = CUSTOMER_PLACEHOLDER_CANONICAL[key];
      if (canonical) return customerVals[canonical] || "";
      const typed = placeholderValues[inner] || placeholderValues[key] || placeholderValues[`${inner}`.trim()];
      if (typed) return typed;
      return original;
    };
    return `${text}`
      .replace(/\{\{\s*([^}]+?)\s*\}\}/g, (full, inner) => resolveInner(inner, full))
      .replace(/\{([^{}]+)\}/g, (full, inner) => resolveInner(inner, full));
  };

  const insertMessagePlaceholder = (current, setter, token) => {
    setter(`${current || ""}${token}`);
  };

  const renderCustomerPlaceholderButtons = (current, setter) => (
    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
      {MESSAGE_CUSTOMER_PLACEHOLDERS.map(({ label, token }) => (
        <button
          type="button"
          key={token}
          className="btn btn-outline-secondary btn-sm"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => insertMessagePlaceholder(current, setter, token)}
          title={token}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const buildOutboundEntries = () => {
    const skipped = [];
    const entries = [];
    selectedRecipients.forEach((recipient) => {
      const customer = findRecipientCustomer(recipient);
      const lang = customer?.preferred_text_language === 'chinese' ? 'chinese' : 'english';
      const rawContent = lang === 'chinese' ? sendChinese : sendEnglish;
      const text = replacePlaceholders(rawContent, customer);
      const phone = getCustomerSmsPhone(customer);
      const name = recipient.label || customer?.name || "Unknown customer";
      if (!`${text || ""}`.trim()) {
        skipped.push(`${name} (empty message)`);
        return;
      }
      if (phone.length < 11) {
        skipped.push(`${name} (no phone)`);
        return;
      }
      entries.push({
        contactPhone: phone,
        text,
        title: sendTitle,
        department: sendTitle || "Center Message",
        from: "center",
        language: lang,
        recipient_name: name,
        recipient_id: recipient.value,
      });
    });
    return { entries, skipped };
  };

  const handleSendNow = async () => {
    if (sending || scheduling) return;
    const { entries, skipped } = buildOutboundEntries();
    if (!entries.length) {
      window.alert(
        skipped.length
          ? `No messages could be sent. ${skipped.join("; ")}`
          : "No messages could be sent. Check that recipients have valid phone numbers."
      );
      return;
    }
    const userName = localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name;
    setSending(true);
    try {
      const response = await MessageService.broadcastMessages({
        title: sendTitle,
        create_by: userName,
        entries,
      });
      const sentCount = Array.isArray(response?.data?.sent) ? response.data.sent.length : 0;
      const errorCount = Array.isArray(response?.data?.errors) ? response.data.errors.length : 0;
      const skippedNote = skipped.length ? `\nSkipped ${skipped.length}: ${skipped.join(", ")}` : "";
      if (errorCount > 0) {
        window.alert(
          `Sent ${sentCount} message part${sentCount === 1 ? "" : "s"} with ${errorCount} error${errorCount === 1 ? "" : "s"}.${skippedNote}`
        );
      } else {
        window.alert(
          `Messages sent to ${entries.length} recipient${entries.length === 1 ? "" : "s"}.${skippedNote}`
        );
      }
      setShowPreviewModal(false);
      setShowScheduleForm(false);
      closeSendModal();
      fetchMessages();
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.message || "Failed to send messages. Please try again.");
    }
    setSending(false);
  };

  const openScheduleForm = () => {
    if (sending || scheduling) return;
    if (!scheduleDate) {
      setScheduleDate(moment().format("YYYY-MM-DD"));
    }
    if (!scheduleTime) {
      setScheduleTime(moment().add(1, "hour").startOf("hour").format("HH:mm"));
    }
    setShowScheduleForm(true);
  };

  const handleScheduleSend = async () => {
    if (sending || scheduling) return;
    if (!`${scheduleDate || ""}`.trim() || !`${scheduleTime || ""}`.trim()) {
      window.alert("Pick a date and time in Eastern Time.");
      return;
    }
    const { entries, skipped } = buildOutboundEntries();
    if (!entries.length) {
      window.alert(
        skipped.length
          ? `No messages could be scheduled. ${skipped.join("; ")}`
          : "No messages could be scheduled. Check that recipients have valid phone numbers."
      );
      return;
    }
    const userName = localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name;
    setScheduling(true);
    try {
      const response = await MessageService.createScheduledMessageJob({
        title: sendTitle,
        create_by: userName,
        schedule_date: scheduleDate,
        schedule_time: scheduleTime,
        entries,
      });
      const skippedNote = skipped.length ? `\nSkipped ${skipped.length}: ${skipped.join(", ")}` : "";
      window.alert(`${response?.data?.message || "Scheduled."}${skippedNote}`);
      setShowPreviewModal(false);
      setShowScheduleForm(false);
      closeSendModal();
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.message || "Failed to schedule messages. Please try again.");
    }
    setScheduling(false);
  };

  const fetchScheduledJobs = () => {
    setLoadingScheduledJobs(true);
    MessageService.getScheduledMessageJobs()
      .then((data) => {
        setScheduledJobs(Array.isArray(data?.data) ? data.data : []);
      })
      .catch(() => {
        setScheduledJobs([]);
        window.alert("Unable to load scheduled jobs.");
      })
      .finally(() => {
        setLoadingScheduledJobs(false);
      });
  };

  const openScheduledJobsModal = () => {
    setShowScheduledJobsModal(true);
    fetchScheduledJobs();
  };

  const handleCancelScheduledJob = async (job) => {
    const id = job?.id;
    if (!id || cancellingJobId) return;
    if (!window.confirm(`Cancel the scheduled send for "${job?.title || "Untitled"}" at ${job?.scheduled_local || ""} ET?`)) {
      return;
    }
    const userName = localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name;
    setCancellingJobId(id);
    try {
      await MessageService.cancelScheduledMessageJob(id, { cancelled_by: userName });
      fetchScheduledJobs();
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.message || "Unable to cancel this job.");
    }
    setCancellingJobId("");
  };

  const recipientPreviews = selectedRecipients.map((recipient, index) => {
    const customer = findRecipientCustomer(recipient);
    const lang = customer?.preferred_text_language === 'chinese' ? 'chinese' : 'english';
    const rawContent = lang === 'chinese' ? sendChinese : sendEnglish;
    const body = replacePlaceholders(rawContent || '', customer);
    return {
      key: recipient.value,
      index: index + 1,
      recipientLabel: recipient.label,
      language: lang,
      body,
    };
  });

  // ---- Filtering & Search for Sent Messages ----
  const allSentMessages = messages
    .filter((m) => {
      if (!(m.title || m.sent_date)) return false;
      return Number(m.site) === Number(EventsService.site);
    })
    .sort((a, b) => {
      const aTime = new Date(a?.sent_date || a?.create_date || 0).getTime();
      const bTime = new Date(b?.sent_date || b?.create_date || 0).getTime();
      return bTime - aTime;
    });

  const filteredSentMessages = allSentMessages.filter(msg => {
    // Search filter
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      const name = (msg?.recipient_name || msg?.message_name || '').toLowerCase();
      const title = (msg?.title || msg?.message_title || '').toLowerCase();
      const content = (msg?.message || msg?.message_body || '').toLowerCase();
      if (!name.includes(q) && !title.includes(q) && !content.includes(q)) return false;
    }
    // Language filter
    if (filterLanguage) {
      if ((msg?.language || '').toLowerCase() !== filterLanguage.toLowerCase()) return false;
    }
    // Date filter
    if (filterDate) {
      if (!msg?.sent_date) return false;
      const msgDate = moment(msg.sent_date).format('MM/DD/YYYY');
      const selectedDateStr = moment(filterDate).format('MM/DD/YYYY');
      if (msgDate !== selectedDateStr) return false;
    }
    return true;
  });

  // ---- Pagination helpers ----
  const paginate = (items, page, pageSize) => {
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  };

  const totalSentPages = Math.ceil(filteredSentMessages.length / sentPageSize) || 1;
  const paginatedSent = paginate(filteredSentMessages, sentPage, sentPageSize);

  const totalTemplatePages = Math.ceil(customTemplates.length / templatePageSize) || 1;
  const paginatedTemplates = paginate(customTemplates, templatePage, templatePageSize);

  const totalAppointmentReminderPages =
    Math.ceil(appointmentReminderRows.length / appointmentReminderPageSize) || 1;
  const paginatedAppointmentReminderRows = paginate(
    appointmentReminderRows,
    appointmentReminderPage,
    appointmentReminderPageSize
  );

  // Reset to page 1 when filters change
  useEffect(() => { setSentPage(1); }, [searchText, filterLanguage, filterDate]);

  const renderPagination = (currentPage, totalPages, setPage, totalItems, pageSize) => {
    if (totalItems === 0) return null;
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }
    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

    return (
      <div className="d-flex align-items-center justify-content-between mt-3" style={{ fontSize: '13px' }}>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          <span>Showing</span>
          <select
            className="form-select form-select-sm"
            style={{ width: '70px' }}
            value={pageSize}
            onChange={(e) => {
              const newSize = parseInt(e.target.value);
              if (setPage === setSentPage) { setSentPageSize(newSize); setSentPage(1); }
              else if (setPage === setAppointmentReminderPage) {
                setAppointmentReminderPageSize(newSize);
                setAppointmentReminderPage(1);
              }
              else { setTemplatePageSize(newSize); setTemplatePage(1); }
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
        <span className="text-muted">Showing {startItem} to {endItem} out of {totalItems} records</span>
        <div className="d-flex align-items-center" style={{ gap: '4px' }}>
          <button className="btn btn-outline-secondary btn-sm" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
            <ChevronLeft size={14} />
          </button>
          {pageNumbers.map(num => (
            <button
              key={num}
              className={`btn btn-sm ${num === currentPage ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setPage(num)}
              style={{ minWidth: '32px' }}
            >
              {num}
            </button>
          ))}
          <button className="btn btn-outline-secondary btn-sm" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    );
  };

  // Get unique languages for the filter dropdown
  const availableLanguages = [...new Set(allSentMessages.map(m => m?.language).filter(Boolean))];
  const appointmentReminderTemplates = {
    chineseStarting: appointmentReminderChineseStarting,
    chineseAppointment: appointmentReminderChinese,
    chineseEnding: appointmentReminderChineseEnding,
    englishStarting: appointmentReminderEnglishStarting,
    englishAppointment: appointmentReminderEnglish,
    englishEnding: appointmentReminderEnglishEnding,
  };
  const appointmentPreview = appointmentPreviewRow
    ? buildAppointmentReminderPreview(appointmentPreviewRow, appointmentReminderTemplates)
    : null;
  const testAppointmentMessages = appointmentPreview?.messages || [];
  const testSmsParts = testAppointmentMessages.flatMap((messageText, appointmentIndex) =>
    splitMessageIntoSmsParts(messageText).map((part, partIndex, parts) => ({
      ...part,
      appointmentIndex,
      appointmentCount: testAppointmentMessages.length,
      partIndex,
      partCount: parts.length,
    }))
  );
  const testSmsCharLength = testAppointmentMessages.reduce(
    (sum, messageText) => sum + unicodeCharLength(messageText),
    0
  );
  const testSelectedRecipientCount =
    (testSendToCustomer ? 1 : 0) + (testSendToCustomNumber ? 1 : 0);
  const testSmsTotalCount = testSmsParts.length * Math.max(testSelectedRecipientCount, 1);

  const closeAppointmentPreview = () => {
    if (sendingTestMessage) return;
    setShowTestMessageModal(false);
    setAppointmentPreviewRow(null);
  };

  const openTestMessageModal = () => {
    setTestSendToCustomer(false);
    setTestSendToCustomNumber(false);
    setTestCustomNumber("");
    setTestSendDebug(null);
    setShowTestMessageModal(true);
  };

  const closeTestMessageModal = () => {
    if (sendingTestMessage) return;
    setShowTestMessageModal(false);
  };

  const handleSendTestMessage = async () => {
    const appointmentTexts = testAppointmentMessages
      .map((text) => `${text || ""}`.trim())
      .filter(Boolean);
    if (!appointmentTexts.length) {
      window.alert("There is no message to send.");
      return;
    }
    if (!testSendToCustomer && !testSendToCustomNumber) {
      window.alert("Select at least one number to send to.");
      return;
    }

    const phones = [];
    if (testSendToCustomer) {
      const customerPhone = toTextMagicPhone(appointmentPreviewRow?.primaryPhone);
      if (customerPhone.length < 11) {
        window.alert("This customer does not have a valid phone number.");
        return;
      }
      phones.push(customerPhone);
    }
    if (testSendToCustomNumber) {
      const customPhone = toTextMagicPhone(testCustomNumber);
      if (customPhone.length < 11) {
        window.alert("Enter a valid phone number to send to.");
        return;
      }
      phones.push(customPhone);
    }

    const uniquePhones = [...new Set(phones)];
    setSendingTestMessage(true);
    setTestSendDebug(null);
    try {
      const response = await MessageService.sendMessage({
        messages: uniquePhones.flatMap((phone) =>
          appointmentTexts.map((text) => ({
            contactPhone: phone,
            text,
            department: "Appointment Reminder Test",
            from: "center",
          }))
        ),
      });
      const payload = {
        success: response?.data?.success !== false,
        message: response?.data?.message || "Message Sent Correctly",
        phones: uniquePhones,
        appointmentMessages: appointmentTexts.length,
        sent: response?.data?.sent || [],
        errors: response?.data?.errors || [],
      };
      console.log("TextMagic send result", payload);
      setTestSendDebug(payload);
    } catch (error) {
      const payload = {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to send test message.",
        phones: uniquePhones,
        appointmentMessages: appointmentTexts.length,
        sent: error?.response?.data?.sent || [],
        errors: error?.response?.data?.errors || [
          {
            message: error?.message || "Failed to send test message.",
            textMagic: error?.response?.data || null,
          },
        ],
        httpStatus: error?.response?.status || null,
      };
      console.log("TextMagic send error", payload, error);
      setTestSendDebug(payload);
    } finally {
      setSendingTestMessage(false);
    }
  };

  const openSendAllModal = () => {
    if (loadingAppointmentReminderList || sendingAllMessages) return;
    if (!appointmentReminderRows.length) {
      window.alert("There are no qualified customers to send to.");
      return;
    }
    setSendAllConfirmText("");
    setShowSendAllModal(true);
  };

  const closeSendAllModal = () => {
    if (sendingAllMessages) return;
    setShowSendAllModal(false);
    setSendAllConfirmText("");
  };

  const handleSendAllMessages = async () => {
    if (sendingAllMessages) return;
    if (`${sendAllConfirmText || ""}`.trim() !== "SEND") {
      window.alert('Type SEND to confirm.');
      return;
    }

    const payloads = [];
    const skipped = [];
    appointmentReminderRows.forEach((row) => {
      const texts = (buildAppointmentReminderPreview(row, appointmentReminderTemplates).messages || [])
        .map((text) => `${text || ""}`.trim())
        .filter(Boolean);
      const phone = toTextMagicPhone(row?.primaryPhone);
      if (phone.length < 11 || texts.length === 0) {
        skipped.push(row?.name || "Unknown customer");
        return;
      }
      texts.forEach((text) => {
        payloads.push({
          contactPhone: phone,
          text,
          department: "Appointment Reminder",
          from: "center",
        });
      });
    });

    if (!payloads.length) {
      window.alert("No messages could be sent. Check that customers have valid phone numbers.");
      return;
    }

    setSendingAllMessages(true);
    try {
      const response = await MessageService.sendMessage({ messages: payloads });
      const sentCount = Array.isArray(response?.data?.sent) ? response.data.sent.length : 0;
      const errorCount = Array.isArray(response?.data?.errors) ? response.data.errors.length : 0;
      const customerCount = appointmentReminderRows.length - skipped.length;
      const skippedNote = skipped.length
        ? `\nSkipped ${skipped.length}: ${skipped.join(", ")}`
        : "";
      if (errorCount > 0) {
        window.alert(
          `Sent ${sentCount} message part${sentCount === 1 ? "" : "s"} with ${errorCount} error${errorCount === 1 ? "" : "s"}.${skippedNote}`
        );
      } else {
        window.alert(
          `Sent ${payloads.length} appointment reminder text${payloads.length === 1 ? "" : "s"} to ${customerCount} customer${customerCount === 1 ? "" : "s"}.${skippedNote}`
        );
      }
      setShowSendAllModal(false);
      setSendAllConfirmText("");
    } catch (error) {
      window.alert(
        error?.response?.data?.message || error?.message || "Failed to send messages. Please try again."
      );
    } finally {
      setSendingAllMessages(false);
    }
  };

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/">General</Breadcrumb.Item>
          <Breadcrumb.Item active>Messaging</Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
           <h4>Message</h4>
        </div>
      </div>
      <div className="app-main-content-list-container">
        <div className="app-main-content-list-func-container">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k);
              if (k === "allMessages") fetchMessages();
            }}
            id="messages-tab"
          >
            <Tab eventKey="allMessages" title="All Sent Messages">
              <table className="personnel-info-table" style={{ marginTop: '16px' }}>
                <thead>
                  <tr>
                    <th className="th-index">No.</th>
                    <th>Recipient Name</th>
                    <th>Message Title</th>
                    <th>Message Language</th>
                    <th>Sent Date</th>
                    <th>Message Content</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSent.map((msg, index) => (
                    <tr key={msg.id}>
                      <td className="td-index">{(sentPage - 1) * sentPageSize + index + 1}</td>
                      <td>{msg?.recipient_name || msg?.message_name || ''}</td>
                      <td>{msg?.title || msg?.message_title || ''}</td>
                      <td>{msg?.language || ''}</td>
                      <td>{msg?.sent_date ? moment(msg.sent_date).format('MM/DD/YYYY') : ''}</td>
                      <td style={{ maxWidth: '400px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg?.message || msg?.message_body || ''}</td>
                    </tr>
                  ))}
                  {filteredSentMessages.length === 0 && (
                    <tr><td colSpan="6" style={{ textAlign: 'center', color: '#999', padding: '24px' }}>No sent messages found.</td></tr>
                  )}
                </tbody>
              </table>
              {renderPagination(sentPage, totalSentPages, setSentPage, filteredSentMessages.length, sentPageSize)}
            </Tab>
            <Tab eventKey="messageTemplate" title="Message Template">
              <table className="personnel-info-table" style={{ marginTop: '16px' }}>
                <thead>
                  <tr>
                    <th className="th-index">No.</th>
                    <th>Template Title</th>
                    <th>Message Content (Chinese)</th>
                    <th>Message Content (English)</th>
                    {canEditMessagingTemplate && <th style={{ width: '80px' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {paginatedTemplates.map((template, index) => (
                    <tr key={template.id}>
                      <td className="td-index">{(templatePage - 1) * templatePageSize + index + 1}</td>
                      <td>{template?.title}</td>
                      <td style={{ maxWidth: '300px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{template?.chinese}</td>
                      <td style={{ maxWidth: '300px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{template?.english}</td>
                      {canEditMessagingTemplate && <td>
                        <PencilSquare size={16} className="clickable me-2" onClick={() => openEditTemplateModal(template)} />
                        <Trash size={16} className="clickable" color="#dc3545" onClick={() => handleDeleteTemplate(template.id)} />
                      </td>}
                    </tr>
                  ))}
                  {customTemplates.length === 0 && (
                    <tr><td colSpan={canEditMessagingTemplate ? 5 : 4} style={{ textAlign: 'center', color: '#999', padding: '24px' }}>No templates yet. Create one to get started.</td></tr>
                  )}
                </tbody>
              </table>
              {renderPagination(templatePage, totalTemplatePages, setTemplatePage, customTemplates.length, templatePageSize)}
            </Tab>
            <Tab eventKey="appointmentReminder" title="Appointment Reminder">
              {autoSendSchedule && (
                <div className="appointment-reminder-auto-send-next mb-3" style={{ marginTop: "8px" }}>
                  {autoSendSchedule.enabled === false ? (
                    <div>Weekly auto send is off for {getSiteDisplayName(EventsService.site)}.</div>
                  ) : (
                    <>
                      <div>
                        <strong>Next sending:</strong> {autoSendSchedule.next_send_local || "—"}
                        {autoSendSchedule.skip_next ? " — next time will be skipped." : ""}
                      </div>
                      {autoSendSchedule.skip_next ? (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="mt-2"
                          disabled={skippingAutoSend || savingAutoSendSchedule}
                          onClick={() => handleSetAutoSendSkip(false)}
                        >
                          {skippingAutoSend ? "Updating…" : "Unskip"}
                        </Button>
                      ) : (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="mt-2"
                          disabled={skippingAutoSend || savingAutoSendSchedule}
                          onClick={() => handleSetAutoSendSkip(true)}
                        >
                          {skippingAutoSend ? "Updating…" : "Skip next time"}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
              {loadingAppointmentReminderList ? (
                <div className="d-flex align-items-center gap-2 p-4 text-muted">
                  <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
                  <span>Loading qualified customers…</span>
                </div>
              ) : (
                <>
                  <table className="personnel-info-table" style={{ marginTop: '16px' }}>
                    <thead>
                      <tr>
                        <th className="th-index">No.</th>
                        <th>Customer</th>
                        <th>Preferred Text Language</th>
                        <th>Primary Phone</th>
                        <th>Next Week Appointments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAppointmentReminderRows.map((row, index) => (
                        <tr key={row.id}>
                          <td className="td-index">
                            {(appointmentReminderPage - 1) * appointmentReminderPageSize + index + 1}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="appointment-reminder-customer-link"
                              onClick={() => setAppointmentPreviewRow(row)}
                            >
                              {row.name}
                            </button>
                          </td>
                          <td>{row.preferredTextLanguage}</td>
                          <td>{row.primaryPhone}</td>
                          <td>{row.appointmentCount}</td>
                        </tr>
                      ))}
                      {appointmentReminderRows.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', color: '#999', padding: '24px' }}>
                            No qualified customers found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {renderPagination(
                    appointmentReminderPage,
                    totalAppointmentReminderPages,
                    setAppointmentReminderPage,
                    appointmentReminderRows.length,
                    appointmentReminderPageSize
                  )}
                </>
              )}
            </Tab>
          </Tabs>
          <div className="list-func-panel" style={{ gap: '8px', alignItems: 'center' }}>
            {activeTab === 'allMessages' && (
              <>
                <div className="d-flex align-items-center" style={{ position: 'relative' }}>
                  <Search size={12} style={{ position: 'absolute', left: '10px', color: '#999' }} />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ paddingLeft: '30px', width: '180px', borderRadius: '6px', height: '31px', fontSize: '13px' }}
                  />
                </div>
                <Dropdown show={showFilterDropdown} onToggle={(isOpen) => { setShowFilterDropdown(isOpen); if (isOpen) setShowDatePicker(false); }}>
                  <Dropdown.Toggle variant="outline-secondary" size="sm">
                    <Filter size={14} className="me-1" />Filter{filterLanguage ? `: ${filterLanguage}` : ''}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item active={!filterLanguage} onClick={() => { setFilterLanguage(''); setShowFilterDropdown(false); }}>All Languages</Dropdown.Item>
                    {availableLanguages.map(lang => (
                      <Dropdown.Item key={lang} active={filterLanguage === lang} onClick={() => { setFilterLanguage(lang); setShowFilterDropdown(false); }}>
                        {lang.charAt(0).toUpperCase() + lang.slice(1)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <div style={{ position: 'relative' }} ref={datePickerRef}>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => { setShowDatePicker(!showDatePicker); setShowFilterDropdown(false); }}
                  >
                    <Calendar3 size={14} className="me-1" />
                    {filterDate ? moment(filterDate).format('MM/DD/YYYY') : 'Select Date to View'}
                  </button>
                  {filterDate && (
                    <button
                      className="btn btn-link btn-sm p-0 ms-1"
                      onClick={(e) => { e.stopPropagation(); setFilterDate(null); setShowDatePicker(false); }}
                      title="Clear date"
                      style={{ fontSize: '14px', textDecoration: 'none', color: '#999' }}
                    >
                      ✕
                    </button>
                  )}
                  {showDatePicker && (
                    <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 1000, marginTop: '4px' }}>
                      <DatePicker
                        selected={filterDate}
                        onChange={(date) => { setFilterDate(date); setShowDatePicker(false); }}
                        inline
                      />
                    </div>
                  )}
                </div>
                <button className="btn btn-outline-secondary btn-sm" onClick={openScheduledJobsModal}>
                  <ClockHistory size={14} className="me-1" />Show Scheduled Jobs
                </button>
                <button className="btn btn-primary btn-sm" onClick={openSendModal}><Send size={14} className="me-1" />Send New Message</button>
              </>
            )}
            {activeTab === 'messageTemplate' && (
              canEditMessagingTemplate && <button className="btn btn-primary btn-sm" onClick={openCreateTemplateModal}><Plus size={16} className="me-1" />Create Message Template</button>
            )}
            {activeTab === 'appointmentReminder' && (
              <>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={
                    loadingAppointmentReminderList ||
                    sendingAllMessages ||
                    appointmentReminderRows.length === 0
                  }
                  onClick={openSendAllModal}
                >
                  <Send size={14} className="me-1" />
                  {sendingAllMessages ? "Sending…" : "Send all messages"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={openAutoSendScheduleModal}
                >
                  <Clock size={14} className="me-1" />
                  Auto Send Schedule
                </button>
                {canEditMessagingTemplate && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowAppointmentTemplateModal(true)}
                  >
                    Change Template
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        show={!!appointmentPreviewRow}
        onHide={closeAppointmentPreview}
        size="lg"
        centered
        enforceFocus={!showTestMessageModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Message Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {appointmentPreviewRow && (
            <>
              <div className="mb-3">
                <div><strong>{appointmentPreviewRow.name}</strong></div>
                <div className="text-muted" style={{ fontSize: '13px' }}>
                  {appointmentPreview?.languageLabel} · {appointmentPreviewRow.primaryPhone || "No phone"} · {(appointmentPreview?.appointments || []).length} appointment{(appointmentPreview?.appointments || []).length === 1 ? "" : "s"} next week
                </div>
              </div>
              <div className="mb-3">
                <h6>Appointments</h6>
                <div className="appointment-reminder-preview-appointments">
                  {(appointmentPreview?.appointments || []).map((appointment, index) => {
                    const start = appointment.start ? moment(appointment.start) : null;
                    const validStart = start?.isValid() ? start : null;
                    return (
                      <div key={`${appointment.start}-${index}`} className="appointment-reminder-preview-appointment">
                        <strong>
                          {(appointmentPreview?.appointments || []).length > 1 ? `Appointment ${index + 1}: ` : ""}
                          {validStart ? `${validStart.format("dddd, MM/DD/YYYY")} at ${validStart.format("h:mm A")}` : "Date/time unavailable"}
                        </strong>
                        <div>Provider: {appointment.providerName || "—"}</div>
                        {appointment.specialty ? <div>Specialty: {appointment.specialty}</div> : null}
                        {appointment.providerAddress ? <div>Address: {appointment.providerAddress}</div> : null}
                        {appointment.requirement ? <div>Special Instructions: {appointment.requirement}</div> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
              <h6>
                {testAppointmentMessages.length > 1
                  ? `Messages (${testAppointmentMessages.length})`
                  : "Message"}
              </h6>
              {testAppointmentMessages.length > 0 ? (
                <div className="appointment-reminder-preview-messages">
                  {testAppointmentMessages.map((messageText, index) => (
                    <div key={`preview-message-${index}`} className="appointment-reminder-preview-message">
                      {testAppointmentMessages.length > 1 ? (
                        <div className="appointment-reminder-preview-message-title">
                          Message {index + 1} of {testAppointmentMessages.length}
                        </div>
                      ) : null}
                      {messageText || "—"}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="appointment-reminder-preview-message">—</div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={closeAppointmentPreview}>
            Close
          </Button>
          <Button variant="primary" onClick={openTestMessageModal}>
            Test Message
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showTestMessageModal}
        onHide={closeTestMessageModal}
        centered
        size="lg"
        scrollable
        className="test-message-modal"
        backdropClassName="test-message-modal-backdrop"
      >
        <Modal.Header closeButton={!sendingTestMessage}>
          <Modal.Title>Test Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="test-message-recipients mb-3">
            <label className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                checked={testSendToCustomer}
                disabled={!digitsOnlyPhone(appointmentPreviewRow?.primaryPhone)}
                onChange={(event) => setTestSendToCustomer(event.target.checked)}
              />
              <span className="form-check-label">
                Send to this customer
                {appointmentPreviewRow?.primaryPhone
                  ? ` (${appointmentPreviewRow.primaryPhone})`
                  : " (no phone)"}
              </span>
            </label>
            <label className="form-check test-message-custom-number">
              <input
                type="checkbox"
                className="form-check-input"
                checked={testSendToCustomNumber}
                onChange={(event) => setTestSendToCustomNumber(event.target.checked)}
              />
              <span className="form-check-label">Send to this number</span>
              <input
                type="tel"
                className="form-control form-control-sm"
                placeholder="(240)555-1234"
                value={testCustomNumber}
                onChange={(event) => {
                  setTestCustomNumber(formatUsPhoneInput(event.target.value));
                  if (!testSendToCustomNumber) setTestSendToCustomNumber(true);
                }}
              />
            </label>
          </div>
          <h6>
            {testAppointmentMessages.length > 1
              ? `Message preview (${testAppointmentMessages.length} separate texts)`
              : "Message preview"}
          </h6>
          {testAppointmentMessages.length > 0 ? (
            <div className="appointment-reminder-preview-messages mb-3">
              {testAppointmentMessages.map((messageText, index) => (
                <div key={`test-preview-message-${index}`} className="appointment-reminder-preview-message">
                  {testAppointmentMessages.length > 1 ? (
                    <div className="appointment-reminder-preview-message-title">
                      Message {index + 1} of {testAppointmentMessages.length}
                    </div>
                  ) : null}
                  {messageText || "—"}
                </div>
              ))}
            </div>
          ) : (
            <div className="appointment-reminder-preview-message mb-3">—</div>
          )}
          <div className="test-message-sms-count mb-2">
            {testSmsParts.length === 0 ? (
              "No message to send."
            ) : (
              <>
                {testAppointmentMessages.length > 1
                  ? `${testAppointmentMessages.length} appointment messages · `
                  : ""}
                Unicode length: {testSmsCharLength} characters total.
                TextMagic limit: {TEXTMAGIC_UNICODE_LIMIT} characters per send.
                {" "}This will be sent as{" "}
                <strong>
                  {testSmsParts.length} text message{testSmsParts.length === 1 ? "" : "s"}
                </strong>
                {testSelectedRecipientCount > 1
                  ? ` to each number (${testSmsTotalCount} total).`
                  : "."}
              </>
            )}
          </div>
          {testSmsParts.length > 0 && (
            <div className="test-message-sms-parts">
              {testSmsParts.map((part, index) => (
                <div key={`sms-part-${index}`} className="test-message-sms-part">
                  <div className="test-message-sms-part-title">
                    {part.appointmentCount > 1
                      ? `Appointment ${part.appointmentIndex + 1} · Text ${part.partIndex + 1} of ${part.partCount}`
                      : `Text ${index + 1} of ${testSmsParts.length}`}
                    <span> · {part.weight}/{TEXTMAGIC_UNICODE_LIMIT} characters</span>
                  </div>
                  <div className="test-message-sms-part-body">{part.text}</div>
                </div>
              ))}
            </div>
          )}
          {testSendDebug && (
            <div className={`test-message-debug ${testSendDebug.success && !(testSendDebug.errors || []).length ? "is-success" : "is-error"}`}>
              <div className="test-message-debug-title">
                {testSendDebug.success && !(testSendDebug.errors || []).length
                  ? "TextMagic response"
                  : "TextMagic send failed"}
              </div>
              <div className="test-message-debug-summary">
                {testSendDebug.message}
                {testSendDebug.httpStatus ? ` · HTTP ${testSendDebug.httpStatus}` : ""}
              </div>
              <pre>{JSON.stringify(testSendDebug, null, 2)}</pre>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={sendingTestMessage}
            onClick={closeTestMessageModal}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={sendingTestMessage || testSmsParts.length === 0}
            onClick={handleSendTestMessage}
          >
            {sendingTestMessage ? "Sending…" : "Send"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSendAllModal}
        onHide={closeSendAllModal}
        centered
        backdrop={sendingAllMessages ? "static" : true}
        keyboard={!sendingAllMessages}
      >
        <Modal.Header closeButton={!sendingAllMessages}>
          <Modal.Title>Send all messages</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            This will send appointment reminder texts to all {appointmentReminderRows.length} qualified
            customer{appointmentReminderRows.length === 1 ? "" : "s"} on this tab. This cannot be undone.
          </p>
          <label className="field-label" htmlFor="send-all-confirm-input">
            Type <strong>SEND</strong> to confirm
          </label>
          <input
            id="send-all-confirm-input"
            type="text"
            className="form-control"
            autoComplete="off"
            autoFocus
            disabled={sendingAllMessages}
            value={sendAllConfirmText}
            onChange={(event) => setSendAllConfirmText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && sendAllConfirmText === "SEND" && !sendingAllMessages) {
                handleSendAllMessages();
              }
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" disabled={sendingAllMessages} onClick={closeSendAllModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={sendingAllMessages || sendAllConfirmText !== "SEND"}
            onClick={handleSendAllMessages}
          >
            {sendingAllMessages ? "Sending…" : "Confirm"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAutoSendScheduleModal}
        onHide={closeAutoSendScheduleModal}
        centered
        backdrop={savingAutoSendSchedule || skippingAutoSend ? "static" : true}
        keyboard={!savingAutoSendSchedule && !skippingAutoSend}
      >
        <Modal.Header closeButton={!savingAutoSendSchedule && !skippingAutoSend}>
          <Modal.Title>Auto Send Schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingAutoSendSchedule ? (
            <div className="d-flex align-items-center justify-content-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading schedule…
            </div>
          ) : (
            <>
              <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                This schedule is only for <strong>{getSiteDisplayName(EventsService.site)}</strong>.
                Other sites keep their own day, time, and enable setting. Repeat weekly in{" "}
                <strong>Eastern Time</strong>, including daylight saving (EST/EDT). At that time the
                app sends appointment reminders for the upcoming week, using the same customers and
                message text as this tab.
              </p>
              <label className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={autoSendEnabled}
                  disabled={savingAutoSendSchedule || skippingAutoSend}
                  onChange={(event) => handleToggleAutoSendEnabled(event.target.checked)}
                />
                <span className="form-check-label">
                  Enable weekly auto send
                </span>
              </label>
              {!autoSendEnabled && (
                <div className="appointment-reminder-skip-notice mb-3">
                  Auto send is off. No future appointment reminder jobs will run for this site until
                  this is enabled again.
                </div>
              )}
              <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                <div>
                  <label className="form-label" style={{ fontSize: "13px", fontWeight: 600 }} htmlFor="auto-send-weekday">
                    Day
                  </label>
                  <select
                    id="auto-send-weekday"
                    className="form-select form-select-sm"
                    value={autoSendWeekday}
                    disabled={savingAutoSendSchedule || skippingAutoSend}
                    onChange={(event) => setAutoSendWeekday(Number(event.target.value))}
                  >
                    {AUTO_SEND_WEEKDAY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: "13px", fontWeight: 600 }} htmlFor="auto-send-time">
                    Time (ET)
                  </label>
                  <input
                    id="auto-send-time"
                    type="time"
                    className="form-control form-control-sm"
                    value={autoSendTime}
                    disabled={savingAutoSendSchedule || skippingAutoSend}
                    onChange={(event) => setAutoSendTime(event.target.value)}
                  />
                </div>
              </div>
              {autoSendEnabled && autoSendSchedule?.next_send_local && (
                <div className="appointment-reminder-auto-send-next">
                  <div>
                    <strong>Next sending:</strong> {autoSendSchedule.next_send_local}
                  </div>
                  {autoSendSchedule.skip_next ? (
                    <>
                      <div className="appointment-reminder-skip-notice mt-2">
                        Next time will be skipped.
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2"
                        disabled={savingAutoSendSchedule || skippingAutoSend}
                        onClick={() => handleSetAutoSendSkip(false)}
                      >
                        {skippingAutoSend ? "Updating…" : "Unskip"}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="mt-2"
                      disabled={savingAutoSendSchedule || skippingAutoSend}
                      onClick={() => handleSetAutoSendSkip(true)}
                    >
                      {skippingAutoSend ? "Updating…" : "Skip next time"}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={savingAutoSendSchedule || skippingAutoSend}
            onClick={closeAutoSendScheduleModal}
          >
            Close
          </Button>
          <Button
            variant="primary"
            disabled={loadingAutoSendSchedule || savingAutoSendSchedule || skippingAutoSend}
            onClick={handleSaveAutoSendSchedule}
          >
            {savingAutoSendSchedule ? "Saving…" : "Save schedule"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAppointmentTemplateModal}
        onHide={() => !savingAppointmentTemplate && setShowAppointmentTemplateModal(false)}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton={!savingAppointmentTemplate}>
          <Modal.Title>Change Appointment Reminder Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="appointment-template-placeholder-list mb-4">
            <div className="field-label mb-2">
              Placeholders — select a template field, then tap a placeholder to insert it.
            </div>
            <div className="d-flex flex-wrap gap-2">
              {APPOINTMENT_REMINDER_PLACEHOLDERS.map(({ label, token }) => (
                <button
                  type="button"
                  key={token}
                  className="btn btn-outline-secondary btn-sm"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => insertAppointmentPlaceholder(token)}
                  title={token}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="mb-4">
            <label className="field-label" htmlFor="appointment-reminder-chinese-starting">
              Chinese starting text
            </label>
            <textarea
              id="appointment-reminder-chinese-starting"
              ref={(element) => {
                appointmentTemplateRefs.current.chineseStarting = element;
              }}
              className="form-control appointment-reminder-template-textarea is-short"
              rows={4}
              value={appointmentReminderChineseStarting}
              onFocus={() => setActiveAppointmentTemplateField("chineseStarting")}
              onChange={(event) => setAppointmentReminderChineseStarting(event.target.value)}
            />
          </div>
          <div className="mb-4">
            <label className="field-label" htmlFor="appointment-reminder-chinese-template">
              Chinese appointment text
            </label>
            <textarea
              id="appointment-reminder-chinese-template"
              ref={(element) => {
                appointmentTemplateRefs.current.chineseAppointment = element;
              }}
              className="form-control appointment-reminder-template-textarea"
              rows={8}
              value={appointmentReminderChinese}
              onFocus={() => setActiveAppointmentTemplateField("chineseAppointment")}
              onChange={(event) => setAppointmentReminderChinese(event.target.value)}
            />
          </div>
          <div className="mb-4">
            <label className="field-label" htmlFor="appointment-reminder-chinese-ending">
              Chinese ending text
            </label>
            <textarea
              id="appointment-reminder-chinese-ending"
              ref={(element) => {
                appointmentTemplateRefs.current.chineseEnding = element;
              }}
              className="form-control appointment-reminder-template-textarea is-short"
              rows={4}
              value={appointmentReminderChineseEnding}
              onFocus={() => setActiveAppointmentTemplateField("chineseEnding")}
              onChange={(event) => setAppointmentReminderChineseEnding(event.target.value)}
            />
          </div>
          <div className="mb-4">
            <label className="field-label" htmlFor="appointment-reminder-english-starting">
              English starting text
            </label>
            <textarea
              id="appointment-reminder-english-starting"
              ref={(element) => {
                appointmentTemplateRefs.current.englishStarting = element;
              }}
              className="form-control appointment-reminder-template-textarea is-short"
              rows={4}
              value={appointmentReminderEnglishStarting}
              onFocus={() => setActiveAppointmentTemplateField("englishStarting")}
              onChange={(event) => setAppointmentReminderEnglishStarting(event.target.value)}
            />
          </div>
          <div className="mb-4">
            <label className="field-label" htmlFor="appointment-reminder-english-template">
              English appointment text
            </label>
            <textarea
              id="appointment-reminder-english-template"
              ref={(element) => {
                appointmentTemplateRefs.current.englishAppointment = element;
              }}
              className="form-control appointment-reminder-template-textarea"
              rows={8}
              value={appointmentReminderEnglish}
              onFocus={() => setActiveAppointmentTemplateField("englishAppointment")}
              onChange={(event) => setAppointmentReminderEnglish(event.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="appointment-reminder-english-ending">
              English ending text
            </label>
            <textarea
              id="appointment-reminder-english-ending"
              ref={(element) => {
                appointmentTemplateRefs.current.englishEnding = element;
              }}
              className="form-control appointment-reminder-template-textarea is-short"
              rows={4}
              value={appointmentReminderEnglishEnding}
              onFocus={() => setActiveAppointmentTemplateField("englishEnding")}
              onChange={(event) => setAppointmentReminderEnglishEnding(event.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={savingAppointmentTemplate}
            onClick={() => setShowAppointmentTemplateModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={savingAppointmentTemplate}
            onClick={saveAppointmentTemplate}
          >
            {savingAppointmentTemplate ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create / Edit Message Template Modal */}
      <Modal show={canEditMessagingTemplate && showCreateTemplateModal} onHide={closeTemplateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingTemplate ? 'Edit Message Template' : 'Create New Message Template'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label"><strong>Template Title</strong> <span className="text-danger">*</span></label>
            <input type="text" className="form-control" placeholder="e.g., Pick Up" value={templateTitle} onChange={(e) => setTemplateTitle(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label"><strong>Message Content (Chinese)</strong> <span className="text-danger">*</span></label>
            <textarea className="form-control" rows={4} placeholder="e.g., 尊敬的{{preferred_name}}：您的今天乘坐的车辆已于{{Time}}出发，司机{{Driver}}，车号{{VehicleNum}}，请及时做好准备准时登车。" value={templateChinese} onChange={(e) => setTemplateChinese(e.target.value)} />
            {renderCustomerPlaceholderButtons(templateChinese, setTemplateChinese)}
            <div className="form-text">Use {"{{token}}"} placeholders. {"{{preferred_name}}"} fills from each recipient.</div>
          </div>
          <div className="mb-3">
            <label className="form-label"><strong>Message Content (English)</strong> <span className="text-danger">*</span></label>
            <textarea className="form-control" rows={4} placeholder="e.g., {{preferred_name}}: Your vehicle today has departed at {{Time}}, driver {{Driver}}, vehicle number {{VehicleNum}}, please be ready to board on time." value={templateEnglish} onChange={(e) => setTemplateEnglish(e.target.value)} />
            {renderCustomerPlaceholderButtons(templateEnglish, setTemplateEnglish)}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleSaveTemplate}>Save</Button>
          <Button variant="light" onClick={closeTemplateModal}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* Send New Message Modal */}
      <Modal show={showSendModal} onHide={closeSendModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Send New Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label"><strong>Select Recipient</strong> <span className="text-danger">*</span></label>
            <MultiSelectDropdown
              value={selectedRecipients}
              onChange={(val) => setSelectedRecipients(val || [])}
              options={eligibleCustomers.map(c => ({ value: c.id, label: c.name }))}
              placeholder="e.g., Recipient Name"
            />
          </div>
          <div className="mb-3">
            <label className="form-label"><strong>Select Template</strong></label>
            <Select
              isClearable
              value={selectedTemplate}
              onChange={handleTemplateSelect}
              options={customTemplates.map(t => ({ value: t.id, label: t.title }))}
              placeholder="e.g., Template 1"
              styles={{
                control: (base) => ({ ...base, borderRadius: '8px' }),
                indicatorSeparator: () => ({ display: 'none' })
              }}
            />
          </div>
          <hr />
          <div className="mb-3">
            <label className="form-label"><strong>Message Title</strong></label>
            <input type="text" className="form-control" placeholder="e.g., Pick Up" value={sendTitle} onChange={(e) => setSendTitle(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label"><strong>Message Content (Chinese)</strong></label>
            <textarea className="form-control" rows={4} placeholder="e.g., 尊敬的{{preferred_name}}：您的今天乘坐的车辆已于{{Time}}出发，司机{{Driver}}，车号{{VehicleNum}}，请及时做好准备准时登车。" value={sendChinese} onChange={(e) => setSendChinese(e.target.value)} />
            {renderCustomerPlaceholderButtons(sendChinese, setSendChinese)}
            <div className="form-text">Use {"{{token}}"} placeholders. {"{{preferred_name}}"} fills from each recipient.</div>
          </div>
          <div className="mb-3">
            <label className="form-label"><strong>Message Content (English)</strong></label>
            <textarea className="form-control" rows={4} placeholder="e.g., {{preferred_name}}: Your vehicle today has departed at {{Time}}, driver {{Driver}}, vehicle number {{VehicleNum}}, please be ready to board on time." value={sendEnglish} onChange={(e) => setSendEnglish(e.target.value)} />
            {renderCustomerPlaceholderButtons(sendEnglish, setSendEnglish)}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={openPreview}><Eye size={16} className="me-2" />Preview</Button>
          <Button variant="light" onClick={closeSendModal}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => { if (!sending && !scheduling) { setShowPreviewModal(false); setShowScheduleForm(false); } }} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Message Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fillInPlaceholders.length > 0 && (
            <div className="mb-4">
              <h6>Fill in Placeholders</h6>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {fillInPlaceholders.map((ph) => (
                  <div key={ph}>
                    <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>{`{{${ph}}}`}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={`Enter ${ph}`}
                      value={placeholderValues[ph] || ''}
                      onChange={(e) => setPlaceholderValues(prev => ({ ...prev, [ph]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <h6>Preview (one per recipient, by preferred text language)</h6>
          <div style={{ maxHeight: 'min(60vh, 520px)', overflowY: 'auto', marginTop: '8px' }}>
            <table className="personnel-info-table">
              <thead>
                <tr>
                  <th className="th-index">No.</th>
                  <th>Recipient</th>
                  <th style={{ width: '100px' }}>Language</th>
                  <th>Message preview</th>
                </tr>
              </thead>
              <tbody>
                {recipientPreviews.map((row) => (
                  <tr key={row.key}>
                    <td className="td-index">{row.index}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{row.recipientLabel}</td>
                    <td>{row.language === 'chinese' ? 'Chinese' : 'English'}</td>
                    <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', padding: '12px 16px' }}>{row.body || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 text-muted" style={{ fontSize: '13px' }}>
            <strong>{selectedRecipients.length}</strong> recipient{selectedRecipients.length !== 1 ? 's' : ''} — each row shows exactly what will be sent to that person.
          </div>
          {showScheduleForm && (
            <div className="mt-3 p-3" style={{ background: '#f8f9fa', borderRadius: '8px' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Clock size={16} />
                <strong>Schedule to send</strong>
              </div>
              <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
                Date and time are <strong>Eastern Time (America/New_York)</strong>, not your computer’s time zone.
              </p>
              <div className="d-flex flex-wrap align-items-end gap-3">
                <div>
                  <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    disabled={scheduling}
                  />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '13px', fontWeight: 600 }}>Time (ET)</label>
                  <input
                    type="time"
                    className="form-control form-control-sm"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    disabled={scheduling}
                  />
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={backToEditing} disabled={sending || scheduling}>
            <ArrowLeft size={16} className="me-2" />Back to Editing
          </Button>
          {showScheduleForm ? (
            <>
              <Button variant="light" onClick={() => setShowScheduleForm(false)} disabled={scheduling}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleScheduleSend} disabled={scheduling || sending}>
                {scheduling ? 'Scheduling...' : 'Confirm Schedule'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline-primary" onClick={openScheduleForm} disabled={sending || scheduling}>
                <Clock size={16} className="me-2" />Schedule to Send
              </Button>
              <Button variant="primary" onClick={handleSendNow} disabled={sending || scheduling}>
                {sending ? 'Sending...' : 'Send Now'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showScheduledJobsModal} onHide={() => setShowScheduledJobsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Scheduled Jobs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingScheduledJobs ? (
            <div className="d-flex align-items-center gap-2 p-3 text-muted">
              <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
              <span>Loading scheduled jobs…</span>
            </div>
          ) : (
            <div style={{ maxHeight: 'min(60vh, 520px)', overflowY: 'auto' }}>
              <table className="personnel-info-table">
                <thead>
                  <tr>
                    <th className="th-index">No.</th>
                    <th>Title</th>
                    <th>Send at (ET)</th>
                    <th>Recipients</th>
                    <th>Status</th>
                    <th>Created by</th>
                    <th style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledJobs.map((job, index) => (
                    <tr key={job.id}>
                      <td className="td-index">{index + 1}</td>
                      <td>{job.title || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{job.scheduled_local ? `${job.scheduled_local} ET` : '—'}</td>
                      <td>{job.recipient_count ?? (job.messages || []).length}</td>
                      <td>{job.status || ''}</td>
                      <td>{job.create_by || ''}</td>
                      <td>
                        {job.status === 'pending' ? (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={!!cancellingJobId}
                            onClick={() => handleCancelScheduledJob(job)}
                          >
                            {cancellingJobId === job.id ? 'Cancelling...' : 'Cancel'}
                          </button>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {scheduledJobs.length === 0 && (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', color: '#999', padding: '24px' }}>
                        No pending scheduled jobs.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowScheduledJobsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  )
};

export default MessageList;
